import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { downloadAuthedFile } from "../lib/download.js";
import type { Customer, Licence } from "../lib/types.js";

/** Admin-only page for generating a signed offline licence file (.lic) for a
 * customer whose device can't reach the licence service - air-gapped or
 * locked-down machines. Moved here from CustomerDetail.tsx so the break-glass
 * action lives in its own place in the nav, separate from day-to-day licence
 * management. Uses the existing GET /admin/licences/:id/offline-licence
 * endpoint and the shared authed-download helper (lib/download.ts). */
export function OfflineActivation() {
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ customer: Customer; licences: Licence[] } | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [busyLicenceId, setBusyLicenceId] = useState<string | null>(null);
  const [licenceError, setLicenceError] = useState<string | null>(null);
  const [downloadedLicenceId, setDownloadedLicenceId] = useState<string | null>(null);
  // Admin-chosen expiry per licence (YYYY-MM-DD). Defaults to the licence's own
  // expiry; the admin can shorten it for a tightly time-boxed break-glass file.
  const [expiryByLicence, setExpiryByLicence] = useState<Record<string, string>>({});

  const today = new Date().toISOString().slice(0, 10);
  const asDate = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : "");

  async function loadCustomers(q = "") {
    try {
      const path = q.trim() ? `/admin/customers?q=${encodeURIComponent(q.trim())}` : "/admin/customers";
      const res = await api.get<{ customers: Customer[] }>(path);
      setCustomers(res.customers);
    } catch (err) {
      setCustomersError(err instanceof Error ? err.message : "Failed to load customers");
    }
  }

  // Debounced search, same pattern as CustomersList.
  useEffect(() => {
    const t = setTimeout(() => loadCustomers(search), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  async function selectCustomer(id: string) {
    setSelectedId(id);
    setDetail(null);
    setDetailError(null);
    setLicenceError(null);
    setDownloadedLicenceId(null);
    try {
      const res = await api.get<{ customer: Customer; licences: Licence[] }>(`/admin/customers/${id}`);
      setDetail(res);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : "Failed to load this customer's licences");
    }
  }

  async function generate(licenceId: string, licenceRef: string, expiry: string) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(expiry)) {
      setLicenceError("Choose an expiry date for the offline licence first.");
      return;
    }
    setBusyLicenceId(licenceId);
    setLicenceError(null);
    setDownloadedLicenceId(null);
    try {
      await downloadAuthedFile(
        `/admin/licences/${licenceId}/offline-licence?expiry=${encodeURIComponent(expiry)}`,
        `ScreenSwytch-offline-${licenceRef}.lic`
      );
      setDownloadedLicenceId(licenceId);
    } catch (err) {
      setLicenceError(err instanceof Error ? err.message : "Couldn't generate the offline licence");
    } finally {
      setBusyLicenceId(null);
    }
  }

  return (
    <div>
      <h1>Offline Activation</h1>
      <p className="muted-subtitle" style={{ margin: "0 0 16px", maxWidth: 680 }}>
        Generates a signed offline licence file for air-gapped or locked-down devices that can't reach the
        licence service. Admin-only, break-glass - the offline file gets no online check-in, so the expiry
        you set below is the only thing that locks it. Set it as short as the situation needs.
      </p>

      <div className="card" style={{ marginBottom: 16, borderColor: "var(--border)" }}>
        <strong style={{ fontSize: 14 }}>Where the customer installs the file</strong>
        <p className="muted-subtitle" style={{ margin: "6px 0 0" }}>
          There is no "import" button in the app (activation is online-only). The file is placed on disk by
          whoever sets up the offline PC:
        </p>
        <ol style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 13.5, lineHeight: 1.7 }}>
          <li>Install ScreenSwytch on the offline PC (this creates the folder below).</li>
          <li>Copy the <code>.lic</code> file we send onto that PC.</li>
          <li>
            Put it here and rename it to exactly <code>licence.lic</code>:
            <div style={{ margin: "4px 0 2px" }}>
              <code className="lic-key">C:\ProgramData\ScreenSwytch\licence.lic</code>
            </div>
          </li>
          <li>Start ScreenSwytch. It reads the file and the PC is licensed until the expiry you set.</li>
        </ol>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <label>
          Find a customer
          <input
            type="search"
            placeholder="Search by name, email, licence code, or device name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search customers"
          />
        </label>
        {customersError && <p className="error-text">{customersError}</p>}
        {!customers && !customersError && <p>Loading…</p>}
        {customers && customers.length === 0 && <p className="muted-subtitle">No customers match your search.</p>}
        {customers && customers.length > 0 && (
          <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 8 }}>
            {customers.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => selectCustomer(c.id)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  marginBottom: 2,
                  border: "none",
                  borderRadius: 6,
                  background: selectedId === c.id ? "var(--row-selected, rgba(90, 110, 220, 0.12))" : "transparent",
                  cursor: "pointer",
                }}
              >
                <strong>{c.name}</strong>
                {c.business_name ? ` · ${c.business_name}` : ""}
                <span className="muted-subtitle" style={{ display: "block", fontSize: 12 }}>
                  {c.email}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedId && (
        <div>
          <h2>{detail?.customer.name ?? "Licences"}</h2>
          {detailError && <p className="error-text">{detailError}</p>}
          {!detail && !detailError && <p>Loading…</p>}
          {detail && detail.licences.length === 0 && <p>This customer has no licences yet.</p>}
          {detail && detail.licences.length > 0 && (
            <table className="data-table">
              <thead>
                <tr>
                  <th className="col-nowrap">Licence</th>
                  <th>Status</th>
                  <th className="num">Seats</th>
                  <th className="col-nowrap">Licence expiry</th>
                  <th className="col-nowrap">Offline file expires</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {detail.licences.map((l) => {
                  const licenceExpiry = asDate(l.expiry_date);
                  // An offline file can't outlive the paid term, so an already
                  // expired licence has no valid date to issue - renew it first.
                  const licenceExpired = !!licenceExpiry && licenceExpiry < today;
                  const expiry = expiryByLicence[l.id] ?? licenceExpiry;
                  return (
                    <tr key={l.id}>
                      <td className="col-nowrap"><code>{l.licence_id}</code></td>
                      <td>
                        <span className={`status-badge status-${l.status}`}>{l.status}</span>
                      </td>
                      <td className="num">{l.seats_purchased}</td>
                      <td className="col-nowrap">{licenceExpiry}</td>
                      <td className="col-nowrap">
                        {licenceExpired ? (
                          <span className="muted-subtitle">Licence expired</span>
                        ) : (
                          <input
                            type="date"
                            value={expiry}
                            min={today}
                            max={licenceExpiry || undefined}
                            onChange={(e) => setExpiryByLicence((m) => ({ ...m, [l.id]: e.target.value }))}
                            aria-label={`Offline licence expiry for ${l.licence_id}`}
                            style={{ margin: 0, fontSize: 13, padding: "6px 8px" }}
                          />
                        )}
                      </td>
                      <td className="col-actions">
                        <button
                          className="secondary"
                          disabled={busyLicenceId === l.id || licenceExpired}
                          title={licenceExpired ? "Renew this licence before issuing an offline file" : undefined}
                          onClick={() => generate(l.id, l.licence_id, expiry)}
                        >
                          {busyLicenceId === l.id ? "Generating…" : "Generate & download .lic"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {licenceError && <p className="error-text">{licenceError}</p>}
          {downloadedLicenceId && (
            <p className="muted-subtitle" style={{ marginTop: 8 }}>
              Downloaded. Send it to the customer and have them save it as{" "}
              <code>licence.lic</code> in <code>C:\ProgramData\ScreenSwytch\</code> on the offline device
              (see the steps at the top). It locks on the expiry you chose.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
